import React, { FunctionComponent, useEffect } from 'react';
import FocusLock from 'react-focus-lock';
import { CSSTransition } from 'react-transition-group';

import { BaseDialogProps, DialogAnimations, DialogFooter, DialogSizes, DialogTypes, DrawerAnimations } from '@components/library/dialog';
import { Dialog, DialogBackground, DialogBody, DialogHeader, DialogInner, DialogOverlay, DialogType, DialogWrapper } from '@components/library/dialog/base_dialog';

/**
 * An accessible dialog component for creating modals or drawers.
 * A dialog should only be used when we need to stop the user from interacting with the rest of the application
 * (think hard about this as it has accessibility implications... don't use a dialog if at all possible).
 * Accessibility requirements can be found at https://www.w3.org/TR/wai-aria-practices-1.2/#dialog_modal
 * @param {BaseDialogProps} props BaseDialogProps
 */
export const BaseDialog: FunctionComponent<BaseDialogProps> = (props) => {
  const {
    animation,
    animationTimeout = 300,
    children,
    closeOnBackgroundClick = true,
    contentCentered,
    dialogBackgroundClassName,
    dialogBodyClassName,
    dialogClassName,
    dialogFooterClassName,
    dialogHeaderClassName,
    dialogInnerClassName,
    dialogOverlayClassName,
    footer,
    header,
    height,
    id,
    loading,
    next,
    onClose,
    open,
    prev,
    setOpen,
    showFooter = true,
    showTitle = true,
    size = DialogSizes.Auto,
    title,
    titleIcon,
    type = DialogTypes.Modal,
    width
  } = props;

  /**
   * Closes, the dialog, removes "dialog-open" CSS class and calls the onClose callback.
   * @returns {void} void
   */
  const handleClose = (): void => {
    // this should be first
    setOpen(false);

    const body = document.getElementsByTagName("body");
    if (body.length > 0) {
      body[0].classList.remove("dialog-open");
    }

    if (onClose) {
      onClose();
    }
  };

  /**
   * It is required dialogs should close on escape. DO NOT REMOVE.
   * https://www.w3.org/TR/wai-aria-practices-1.2/#dialog_modal
   * @param {KeyboardEvent} e KeyboardEvent
   * @returns {void} void
   */
  const handleCloseOnEsc = (e: KeyboardEvent): void => {
    // Esc is for IE11
    if (e.key === 'Escape' || e.key === 'Esc') {
      handleClose();
    }
  };

  useEffect(() => {
    // DO NOT REMOVE
    window.addEventListener("keyup", handleCloseOnEsc);
    return (): void => {
      window.removeEventListener("keyup", handleCloseOnEsc);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Adds "dialog-open" CSS class, which is needed to add overflow: hidden on the body element.
   * This prevents vertical scrolling and makes background content inert in accordiance with w3c guidelines.
   * https://www.w3.org/TR/wai-aria-practices-1.2/#dialog_modal
   * @returns {void} void
   */
  const handleOpen = (): void => {
    const body = document.getElementsByTagName("body");
    if (body.length > 0) {
      // there should be only one <body> element
      body[0].classList.add("dialog-open");
    }
  };

  useEffect(() => {
    if (open) {
      handleOpen();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ariaLabelledBy = `${id}-label`;
  const animate = animation || (type === DialogTypes.Modal ? DialogAnimations.FadeInFromTop : DrawerAnimations.FadeInFromRight);

  return (
    <CSSTransition
      classNames={animate}
      in={open}
      mountOnEnter
      timeout={animationTimeout}
      unmountOnExit
    >
      <Dialog className={dialogClassName}>
        <DialogWrapper>
          <DialogType
            animation={animate}
            aria-labelledby={ariaLabelledBy}
            height={height}
            id={id}
            size={size}
            type={type}
            width={width}
          >
            <FocusLock>
              {!!prev && prev}
              <DialogInner className={dialogInnerClassName}>
                <DialogHeader
                  className={dialogHeaderClassName}
                  handleClose={handleClose}
                  id={ariaLabelledBy}
                  showTitle={showTitle}
                  title={title}
                  titleIcon={titleIcon}
                >
                  {header}
                </DialogHeader>
                <DialogBody className={dialogBodyClassName} contentCentered={contentCentered} loading={loading}>
                  {children}
                </DialogBody>
                {showFooter && (
                  <DialogFooter className={dialogFooterClassName}>{footer}</DialogFooter>
                )}
              </DialogInner>
              {!!next && next}
            </FocusLock>
          </DialogType>
          {closeOnBackgroundClick && <DialogBackground className={dialogBackgroundClassName} handleClose={handleClose} id={id} />}
        </DialogWrapper>
        <DialogOverlay className={dialogOverlayClassName} size={size} />
      </Dialog>
    </CSSTransition>
  );
};
